import React, { createElement, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Helpers } from '../../core/src/helpers';

function useStaticContent() {
    const ref = useRef(null);
    const [render, setRender] = useState(typeof window === 'undefined');

    useEffect(() => {
        // check if the innerHTML is empty as client side navigation
        // need to render the component without server-side backup
        const isEmpty = ref.current.innerHTML === '';
        if (isEmpty) {
            setRender(true);
        }
    }, []);

    return [render, ref];
}

/**
A component for skipping client side hydration for content that will not change on the initail load.
This can helper reduce the overhead of hydating larger server side rendered html
@examples
```jsx
<StaticContent>
  <Disclaimer />
</StaticContent>
```
@component StaticContent
@import StaticContent
@returns {ReactElement} The rendered component
*/
export default function StaticContent({ children, element = 'div', ...props }) {
    const [render, ref] = useStaticContent();

    // if we're in the server or a spa navigation, just render it
    if (render) {
        return createElement(element, {
            ...props,
            children,
        });
    }

    // avoid re-render on the client
    return createElement(element, {
        ...props,
        ref,
        suppressHydrationWarning: true,
        dangerouslySetInnerHTML: { __html: '' },
    });
}

StaticContent.propTypes = {
    /**
  Children to render
 */
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),

    /**
  DOM tag type used to create the static wrapper. 
  */
    element: PropTypes.string,
};

StaticContent.defaultProps = {
    element: 'div',
};

export const withStaticContent = Component => {
    return class extends React.PureComponent {
        render() {
            return (
                <StaticContent>
                    <Component {...this.props} />
                </StaticContent>
            );
        }
    };
};

export const StaticHtml = ({ content, css, className }) => {
    if (!content && !css) {
        return null;
    }
    return (
        <StaticContent>
            <div className={className}>
                {css && <style dangerouslySetInnerHTML={Helpers.createMarkup(css)}></style>}
                <div dangerouslySetInnerHTML={Helpers.createMarkup(content)} />
            </div>
        </StaticContent>
    );
};
